import getConfig from "@/utils/getConfig";
import {GetOverwolfUserStatsRewardCategoryEnum, OverwolfApi, OWUserStat, Token} from "@devour/client";
import {useQuery} from "@tanstack/react-query";

interface GetDevourPlayUserStatsResponse {
    message: string;
    userStats: OWUserStat[];
    uniqueGameIds: string[];
    pagination?: {
        page: number;
        pageSize: number;
        total: number;
        totalPages: number;
    };
}

async function fetchDevourPlayUserStats(
    fullToken: Token,
    gameId?: string,
    rewardCategory?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
): Promise<GetDevourPlayUserStatsResponse> {
    const params: any = {};

    // Only add parameters if they're actually provided (not undefined)
    if (page !== undefined) {
        params.page = page;
    }
    if (pageSize !== undefined) {
        params.pageSize = pageSize;
    }
    if (daysBack !== undefined) {
        params.daysBack = daysBack;
    }
    if (gameId) {
        params.gameId = gameId;
    }
    if (rewardCategory) {
        params.rewardCategory = rewardCategory;
    }

    // Use the DevourPlay-specific endpoint with the same API client pattern as the original
    const config = getConfig(fullToken);
    const response = await fetch(`${config.basePath}/devourplay/user-quest-stats?${new URLSearchParams(params)}`, {
        headers: {
            ...config.baseOptions?.headers,
            "Content-Type": "application/json",
            "Authorization": `Bearer ${fullToken.token}`,
        },
    });

    if (!response.ok) {
        if (response.status === 400 || response.status === 401) {
            console.warn(`Authentication error for DevourPlay user quest stats: ${response.status}`);
            return { message: "Authentication required", userStats: [], uniqueGameIds: [] };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
}

function useGetDevourPlayUserStatsQuery(
    fullToken: Token,
    gameId?: string,
    enabled?: boolean,
    category?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
) {
    return {
        queryKey: [
            "devourplay-user-quest-stats",
            fullToken?.id ?? "unauthenticated",
            gameId || "all-games",
            category || "all-categories",
            page,
            pageSize,
            daysBack,
        ],
        queryFn: () => fullToken ? fetchDevourPlayUserStats(fullToken, gameId, category, page, pageSize, daysBack) : null,
        staleTime: 10 * 1000, // Consider data fresh for 10 seconds - more responsive to updates
        gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes (was cacheTime: 0)
        enabled: enabled && !!fullToken, // Allow queries without gameId to get all stats
        refetchOnWindowFocus: true, // Refetch when tab gains focus to catch external updates
        refetchOnReconnect: "always", // Always refetch on reconnection
    } as const;
}

export default function useGetDevourPlayUserStats(
    fullToken: Token,
    gameId?: string,
    enabled: boolean = true,
    category?: GetOverwolfUserStatsRewardCategoryEnum,
    page?: number,
    pageSize?: number,
    daysBack?: number,
) {
    return useQuery<GetDevourPlayUserStatsResponse>(useGetDevourPlayUserStatsQuery(fullToken, gameId, enabled, category, page, pageSize, daysBack));
}